import { Box, Grid, Theme, createStyles, makeStyles } from '@material-ui/core';
import { faPlus, faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InvoiceTipDistribution } from '@spike/invoice-model';
import { AlertMessage } from 'components/UI/V3/AlertMessage';
import clsx from 'clsx';
import { FunctionComponent, useEffect } from 'react';
import { reduceResolution, wbp } from 'Theme';
import Section from '../../Section';
import GridHeader from './GridHeader';
import TipDistributionRow from './TipDistributionRow';
import { useTipDistribution } from './useTipDistribution';

interface TipDistributionProps {
    invoiceId: number;
    tipDistribution: Array<InvoiceTipDistribution>;
    baseStaffIds: ReadonlySet<number>;
    tipsManual?: boolean;
    loading?: boolean;
    className?: string;
    warning?: string;
    tipResetTrigger?: number;
    clearAddedRowsTrigger?: number;
    redistributeKey?: number;
    redistributeAmount?: string;
    initialRowAmount?: string;
    onSaving?: (saving: boolean) => void;
    onTotalChange?: (total: string) => void;
    onRowAdded?: () => void;
    onHasAddedRowsChange?: (hasAdded: boolean) => void;
    onHasAssignedStaffChange?: (hasStaff: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        '@keyframes shimmer': {
            '0%': { backgroundPosition: '-468px 0' },
            '100%': { backgroundPosition: '468px 0' }
        },
        'header': {
            backgroundColor: '#FFF',
            borderBottom: '2px solid black',
            [theme.breakpoints.down(wbp)]: {
                paddingTop: `${reduceResolution(0)}px`,
                paddingBottom: `${reduceResolution(14)}px`,
                marginBottom: `${reduceResolution(22)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingTop: '0px',
                paddingBottom: '18px',
                marginBottom: '22px'
            }
        },
        'row': {
            width: '100%'
        },
        'actions': {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
        },
        'addButton': {
            'display': 'inline-flex',
            'alignItems': 'center',
            'gap': 8,
            'background': '#92B4A7',
            'color': '#FFF',
            'border': 'none',
            'borderRadius': '100px',
            'padding': '12px 20px',
            'fontFamily': 'Poppins',
            'fontSize': 14,
            'fontWeight': 500,
            'lineHeight': '21px',
            'cursor': 'pointer',
            'marginTop': 6,
            '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed'
            }
        },
        'warning': {
            width: 'fit-content',
            maxWidth: '100%',
            marginTop: 12,
            color: '#7A5B1A',
            fontSize: 13,
            fontFamily: 'Poppins',
            fontWeight: 500
        },
        'skeletonRow': {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #DDDDDD',
            borderRadius: '12px',
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(13)}px ${reduceResolution(18)}px`,
                marginBottom: `${reduceResolution(17)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '13px 18px',
                marginBottom: '17px'
            }
        },
        'skeletonBox': {
            height: 20,
            borderRadius: 4,
            background: 'linear-gradient(to right, #f0f0f0 8%, #e0e0e0 18%, #f0f0f0 33%)',
            backgroundSize: '800px 104px',
            animation: '$shimmer 1.5s infinite linear'
        },
        'skeletonStaff': {
            width: '40%'
        },
        'skeletonAmount': {
            width: 60
        }
    })
);

const TipDistribution: FunctionComponent<TipDistributionProps> = props => {
    const classes = useStyles();

    const {
        rows,
        hasUnassignedAddedRow,
        hasAssignedStaff,
        noStaffLeft,
        isSaving,
        hasSettled,
        hasHadRows,
        buildAvailableStaffFor,
        handleAmountChange,
        handleSelectStaff,
        handleRemove,
        handleAddRow
    } = useTipDistribution({
        invoiceId: props.invoiceId,
        tipDistribution: props.tipDistribution,
        baseStaffIds: props.baseStaffIds,
        tipsManual: props.tipsManual,
        tipResetTrigger: props.tipResetTrigger,
        clearAddedRowsTrigger: props.clearAddedRowsTrigger,
        redistributeKey: props.redistributeKey,
        redistributeAmount: props.redistributeAmount,
        initialAddedRowAmount: props.initialRowAmount,
        onSaving: props.onSaving,
        onTotalChange: props.onTotalChange,
        onRowAdded: props.onRowAdded,
        onHasAddedRowsChange: props.onHasAddedRowsChange
    });

    useEffect(() => {
        props.onHasAssignedStaffChange?.(hasAssignedStaff);
    }, [hasAssignedStaff]);

    const showSkeleton =
        !hasSettled ||
        (rows.length === 0 && !hasHadRows && (!!props.loading || isSaving || props.tipDistribution.length > 0));

    return (
        <Section
            title="Tip Distribution"
            className={props.className}
        >
            <Grid
                container
                className={clsx(classes.header)}
            >
                <GridHeader
                    xs={9}
                    title="Staff Member"
                />
                <GridHeader
                    xs={3}
                    title="Amount"
                    align="right"
                />
            </Grid>
            {showSkeleton ? (
                [0, 1].map(index => (
                    <Box
                        className={classes.skeletonRow}
                        key={`skeleton-${index}`}
                    >
                        <Box className={clsx(classes.skeletonBox, classes.skeletonStaff)} />
                        <Box className={clsx(classes.skeletonBox, classes.skeletonAmount)} />
                    </Box>
                ))
            ) : (
                <>
                    {rows.map((row, index) => (
                        <Box
                            className={classes.row}
                            key={row.uuid}
                        >
                            <TipDistributionRow
                                index={index}
                                row={row}
                                availableStaff={buildAvailableStaffFor(row.uuid)}
                                loading={props.loading}
                                onAmountChange={amount => handleAmountChange(row.uuid, amount)}
                                onSelectStaff={staff => handleSelectStaff(row.uuid, staff)}
                                onRemove={
                                    props.tipsManual || (row.source === 'added' && row.id === null)
                                        ? () => handleRemove(row.uuid)
                                        : undefined
                                }
                            />
                        </Box>
                    ))}
                    <Box className={classes.actions}>
                        <button
                            type="button"
                            className={classes.addButton}
                            disabled={hasUnassignedAddedRow || noStaffLeft || props.loading}
                            onClick={handleAddRow}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Add Staff Member
                        </button>
                        {props.warning && (
                            <AlertMessage
                                className={classes.warning}
                                icon={faExclamationTriangle}
                                bgColor="#FFF8EC"
                                bdColor="#F5C462"
                                iconColor="#7A5B1A"
                            >
                                {props.warning}
                            </AlertMessage>
                        )}
                    </Box>
                </>
            )}
        </Section>
    );
};

export default TipDistribution;
